import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import { ChevronDown, ChevronRight, LayoutGrid, MonitorSmartphone, Pencil, RefreshCw, Search, Users, X } from 'lucide-react';

const PERSON_TYPE_LABEL = { driver: 'Driver', staff: 'Staff', passenger: 'Passenger' };
const PERSON_TYPE_CLS = {
  driver: 'bg-sky-100 text-sky-800 border-sky-300 dark:bg-sky-900/40 dark:text-sky-100 dark:border-sky-700',
  staff: 'bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-900/40 dark:text-emerald-100 dark:border-emerald-700',
  passenger: 'bg-purple-100 text-purple-800 border-purple-300 dark:bg-purple-900/40 dark:text-purple-100 dark:border-purple-700',
};

const peopleSummary = (people) => {
  const counts = people.reduce((acc, p) => { acc[p.type] = (acc[p.type] || 0) + 1; return acc; }, {});
  return ['driver', 'staff', 'passenger']
    .filter((t) => counts[t])
    .map((t) => `${counts[t]} ${PERSON_TYPE_LABEL[t]}${counts[t] === 1 ? '' : 's'}`)
    .join(', ');
};

export const AllocationOverview = ({ api, onEdit, refreshToken }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedIds, setExpandedIds] = useState([]);
  const [search, setSearch] = useState('');

  const fetchOverview = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/sub-accounts/allocation-overview');
      setRows(res.data?.sub_accounts || []);
    } catch (err) {
      if (err?.response?.status !== 403) {
        setError(err?.response?.data?.detail || 'Failed to load allocation overview');
      }
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchOverview(); }, [fetchOverview, refreshToken]);

  const q = search.trim().toLowerCase();
  const filtered = q
    ? rows.filter((r) =>
        (r.name || '').toLowerCase().includes(q) ||
        (r.email || '').toLowerCase().includes(q) ||
        r.devices.some((d) => (d.name || '').toLowerCase().includes(q)) ||
        r.people.some((p) => (p.name || '').toLowerCase().includes(q)))
    : rows;

  if (!loading && rows.length === 0 && !error) return null;

  return (
    <Card data-testid="allocation-overview-card">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <LayoutGrid className="w-5 h-5 text-foreground" />
          <CardTitle className="text-base">Allocation Overview</CardTitle>
        </div>
        <CardDescription>
          Every Sub Account with its allocated Devices and People side by side. Click a row to expand, or Edit to change allocations.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search sub account, device or person..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 pl-8 pr-8"
              data-testid="allocation-overview-search"
            />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-testid="allocation-overview-clear-search">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <Button variant="outline" size="sm" onClick={fetchOverview} disabled={loading} data-testid="allocation-overview-refresh">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
        {error && <p className="text-sm text-red-600 dark:text-red-400" data-testid="allocation-overview-error">{error}</p>}
        <Table data-testid="allocation-overview-table">
          <TableHeader>
            <TableRow className="text-xs">
              <TableHead className="w-8" />
              <TableHead>Sub Account</TableHead>
              <TableHead>Devices</TableHead>
              <TableHead>People</TableHead>
              <TableHead className="text-right w-20">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && filtered.length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center py-6 text-muted-foreground text-sm" data-testid="allocation-overview-loading">Loading allocations…</TableCell></TableRow>
            )}
            {!loading && filtered.length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center py-6 text-muted-foreground text-sm" data-testid="allocation-overview-empty">No sub accounts match your search.</TableCell></TableRow>
            )}
            {filtered.map((sa, idx) => {
              const expanded = expandedIds.includes(sa.id);
              return (
                <React.Fragment key={sa.id}>
                  <TableRow
                    className="cursor-pointer hover:bg-muted/50 text-sm"
                    onClick={() => setExpandedIds((prev) => expanded ? prev.filter((id) => id !== sa.id) : [...prev, sa.id])}
                    data-testid={`allocation-overview-row-${idx}`}
                  >
                    <TableCell className="pr-0">
                      {expanded ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{sa.name}</div>
                      <div className="text-xs text-muted-foreground">{sa.email}</div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5 text-xs">
                        <MonitorSmartphone className="w-3.5 h-3.5 text-muted-foreground" />
                        <span data-testid={`allocation-overview-devices-count-${idx}`}>
                          {sa.devices.length === 0 ? <span className="text-muted-foreground italic">None allocated</span> : `${sa.devices.length} device${sa.devices.length === 1 ? '' : 's'}`}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5 text-xs">
                        <Users className="w-3.5 h-3.5 text-muted-foreground" />
                        <span data-testid={`allocation-overview-people-count-${idx}`}>
                          {sa.people.length === 0 ? <span className="text-muted-foreground italic">None allocated</span> : peopleSummary(sa.people)}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <Button variant="outline" size="sm" className="h-7" onClick={() => onEdit?.(sa)} title="Edit allocations" data-testid={`allocation-overview-edit-${idx}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                  {expanded && (
                    <TableRow className="bg-muted/30 hover:bg-muted/30" data-testid={`allocation-overview-detail-${idx}`}>
                      <TableCell />
                      <TableCell colSpan={4} className="py-3">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <p className="text-xs font-medium text-muted-foreground mb-1.5">Devices ({sa.devices.length})</p>
                            <div className="flex flex-wrap gap-1.5">
                              {sa.devices.length === 0 && <span className="text-xs text-muted-foreground italic">No devices allocated</span>}
                              {sa.devices.map((d) => (
                                <Badge key={d.id} variant="outline" className="text-xs font-normal">{d.name}</Badge>
                              ))}
                            </div>
                          </div>
                          <div>
                            <p className="text-xs font-medium text-muted-foreground mb-1.5">People ({sa.people.length})</p>
                            <div className="flex flex-wrap gap-1.5">
                              {sa.people.length === 0 && <span className="text-xs text-muted-foreground italic">No people allocated</span>}
                              {sa.people.map((p) => (
                                <Badge key={p.id} className={`border text-xs font-normal ${PERSON_TYPE_CLS[p.type] || ''}`}>
                                  {p.name} · {PERSON_TYPE_LABEL[p.type] || p.type}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

export default AllocationOverview;
